import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { csrfHeaders } from "./csrf";
import { url } from "./url";

const HEARTBEAT_INTERVAL_MS = 30_000;
const IDLE_AFTER_MS = 60_000;
const PUBLIC_ROUTES = new Set([
  "/",
  "/unauthorized",
  "/exhibition-form",
  "/forgot-password",
  "/reset-password",
  "/mfa-verify",
]);

function resolveEnquiryId(pathname, search) {
  const params = new URLSearchParams(search || "");
  const fromQuery = params.get("id") || params.get("enquiry_id");
  if (fromQuery && /^\d+$/.test(fromQuery)) {
    return Number(fromQuery);
  }
  return null;
}

/**
 * Sends authenticated presence heartbeats so admins can see
 * idle time and current page for online staff.
 * Uses a quiet fetch so transient auth failures do not force logout.
 */
export default function PresenceHeartbeat() {
  const location = useLocation();
  const lastActivityRef = useRef(Date.now());
  const pathRef = useRef(`${location.pathname}${location.search || ""}`);

  useEffect(() => {
    pathRef.current = `${location.pathname}${location.search || ""}`;
  }, [location.pathname, location.search]);

  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "visibilitychange"];
    events.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
    return () => {
      events.forEach((evt) => window.removeEventListener(evt, markActive));
    };
  }, []);

  useEffect(() => {
    const apiBase = (url || "").replace(/\/+$/, "");
    if (!apiBase) return undefined;

    let cancelled = false;

    const send = async () => {
      if (cancelled) return;
      if (PUBLIC_ROUTES.has(location.pathname)) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }

      const path = pathRef.current || `${window.location.pathname}${window.location.search || ""}`;
      const idle = Date.now() - lastActivityRef.current >= IDLE_AFTER_MS;
      const enquiryId = resolveEnquiryId(location.pathname, location.search);

      try {
        await fetch(`${apiBase}/presence/heartbeat`, {
          method: "POST",
          credentials: "include",
          headers: csrfHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            path,
            idle,
            enquiry_id: enquiryId,
          }),
        });
      } catch {
        // Presence is best-effort; ignore network failures.
      }
    };

    send();
    const intervalId = window.setInterval(send, HEARTBEAT_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [location.pathname, location.search]);

  return null;
}
